import { forwardRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useFileCompression } from './hooks/useFileCompression';
import { LoadingButton } from '@mui/lab';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileUpload = forwardRef(
  (
    {
      accept = null,
      children,
      component = null,
      compressFile = false,
      file = null,
      onChangeUpload = null,
      onChange = null,
      ...props
    },
    ref,
  ) => {
    const { outputFile, onHandleFileUpload, isLoading } = useFileCompression({
      compressFile: compressFile,
    }); // Hook con lógica de compresión de archivos

    const ComponentUpload = ({ children }) => {
      switch (component) {
        case 'Button' || 'IconButton': {
          const Tag = component;
          return (
            <Tag {...props} component='label'>
              {children}
            </Tag>
          );
        }
        default:
          return (
            <LoadingButton
              {...props}
              loading={isLoading}
              loadingPosition='center'
              component='label'
            >
              {children}
            </LoadingButton>
          );
      }
    };

    useEffect(() => {
      onHandleReturnOutputFile(outputFile);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputFile]);

    /**
     * Devuelve el archivo procesado a traves de un objeto
     * Event.target.files[]
     */
    const onHandleReturnOutputFile = async (file) => {
      if (file) {
        const evento = new Event('change');
        Object.defineProperty(evento, 'target', {
          writable: false,
          value: {
            files: [file],
          },
        });

        if (onChangeUpload) {
          onChangeUpload(evento);
        }

        if (onChange) {
          const validated = await validateFile(file);
          if (validated) {
            onChange(validated);
          }
        }
      }
    };

    const validateFile = async (file) => {
      if (!file) return null;

      // Validación de tipo de archivo (case-insensitive)
      if (accept) {
        // accept is array or string
        const acceptedTypes = Array.isArray(accept)
          ? accept.map((type) => type.trim().toLowerCase())
          : accept.split(',').map((type) => type.trim().toLowerCase());

        const fileType = file.type ? file.type.toLowerCase() : '';
        const fileName = file.name ? file.name.toLowerCase() : '';

        const isAccepted = acceptedTypes.some(
          (type) => fileType === type || (type.startsWith('.') && fileName.endsWith(type)),
        );

        if (!isAccepted) {
          // setError('Por favor selecciona un archivo válido (pdf, jpg, png, etc).');
          return null;
        }
      }

      return await getFileData(file);
    };

    const getFileData = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const fileExtension = file.name.split('.').pop();
          resolve({
            fileName: file.name,
            extension: '.' + fileExtension,
            dimension: file.size,
            contentBase64: reader.result.split(',')[1],
          });
        };
        reader.readAsDataURL(file);
      });
    };

    return (
      <>
        <ComponentUpload>
          {children}
          <VisuallyHiddenInput accept={accept} capture type='file' onChange={onHandleFileUpload} />
        </ComponentUpload>
      </>
    );
  },
);

export default FileUpload;
